import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * CustomerProtectedRoute
 *
 * Mirrors the existing admin `ProtectedRoute` but for customer-only pages
 * (`/home`, `/bookings`, `/profile`, `/book`, etc.).
 *
 * Why this exists:
 *   Before this wrapper, each customer page implemented its own auth guard
 *   inline as `useEffect(() => { if (!user) navigate('/') }, [user])`.
 *   On a cold reload, `AuthProvider` starts with `user === null`
 *   (`useState(null)`) and only hydrates from localStorage inside its own
 *   effect. The customer page's effect runs on the *first* render — sees
 *   `user=null`, schedules `setTimeout(navigate, 0)`. On slower paths the
 *   macrotask wins the race against the cleanup `clearTimeout`, so a
 *   logged-in customer hitting F5 on `/home` was getting bounced to `/`
 *   even though their tokens were intact. Customers (rightly) called this
 *   "I got logged out".
 *
 * Fix:
 *   Centralise the guard. AuthProvider exposes `loading` precisely so we
 *   can wait for hydration to complete; this wrapper does that once, in
 *   one place. The next customer page someone adds can't reintroduce the
 *   bug by accident.
 *
 *   - While AuthContext is still hydrating from localStorage → render
 *     nothing (no redirect, no flash, no API calls).
 *   - Hydration done AND no user → redirect to `redirectTo` (default `/`).
 *   - Hydration done AND user present → render the protected page.
 */
export default function CustomerProtectedRoute({ children, redirectTo = '/' }) {
  const { user, loading } = useAuth();

  // Wait for AuthProvider's initial localStorage hydration. Without this
  // gate, the very first render of any customer page would see
  // `user === null` and trigger a redirect that races the hydration —
  // which is exactly the F5-kicks-customer-to-landing-page bug.
  if (loading) return null;

  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}
